import type {
  Breadcrumb,
  ErrorEvent,
  ReplayFrameEvent,
  init,
} from "@sentry/browser";

type BrowserOptions = NonNullable<Parameters<typeof init>[0]>;
type SpanJson = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];
type TransactionEvent = Parameters<
  NonNullable<BrowserOptions["beforeSendTransaction"]>
>[0];

/** Breadcrumb categories whose `data.url` is the request URL. */
const REQUEST_BREADCRUMB_CATEGORIES = new Set(["fetch", "xhr"]);
/** Replay span ops whose `description` is a resource URL: fetch/XHR requests
 * as well as images, PDF range requests and other loaded assets. */
const RESOURCE_SPAN_OP_PREFIX = "resource.";
const REPLAY_SPAN_TAG = "performanceSpan";
/** Tracing span attributes that carry the request URL, plus the two that
 * carry its query string and fragment separately. */
const SPAN_URL_ATTRIBUTES = ["url", "http.url"];
const SPAN_URL_PART_ATTRIBUTES = ["http.query", "http.fragment"];
/** Inside free text a URL runs until whitespace or a delimiter that cannot
 * appear in it unescaped. */
const URL_TAIL_PATTERN = "[^\\s\"'<>()\\[\\]]*";

export type SupabaseUrlRedactor = ReturnType<typeof createSupabaseUrlRedactor>;

/**
 * Supabase signs storage downloads and auth links with a token in the query
 * string. Telemetry records request URLs in fetch/XHR breadcrumbs, replay
 * network and resource spans, tracing spans, and in error text quoting a
 * failed request, so anyone with access to the telemetry project could reuse
 * a still-valid signed URL. Strip the query and fragment from every URL on the
 * Supabase origin before it leaves the browser. The path (bucket and object)
 * stays: it helps debugging and grants nothing by itself.
 */
export function createSupabaseUrlRedactor(supabaseUrl: string | undefined) {
  const origin = parseOrigin(supabaseUrl);
  const urlPattern = origin
    ? new RegExp(escapeRegExp(origin) + URL_TAIL_PATTERN, "g")
    : null;

  function redactUrl(value: string): string {
    if (!origin) {
      return value;
    }
    try {
      const url = new URL(value);
      if (url.origin !== origin || (!url.search && !url.hash)) {
        return value;
      }
      url.search = "";
      url.hash = "";
      return url.toString();
    } catch {
      return value;
    }
  }

  /** Redact every Supabase URL embedded in free text, such as console output
   * or an exception message that quotes the failed request. */
  function redactText(value: string): string {
    if (!urlPattern) {
      return value;
    }
    return value.replace(urlPattern, (match) => redactUrl(match));
  }

  function redactStrings(value: unknown): unknown {
    if (typeof value === "string") {
      return redactText(value);
    }
    if (Array.isArray(value)) {
      return value.map(redactStrings);
    }
    return value;
  }

  function redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    let data = breadcrumb.data;
    const url: unknown = data?.url;
    if (
      REQUEST_BREADCRUMB_CATEGORIES.has(breadcrumb.category ?? "") &&
      typeof url === "string"
    ) {
      data = { ...data, url: redactUrl(url) };
    }
    // Console breadcrumbs keep the logged arguments alongside the message.
    if (Array.isArray(data?.arguments)) {
      data = { ...data, arguments: redactStrings(data.arguments) };
    }
    const message = breadcrumb.message
      ? redactText(breadcrumb.message)
      : breadcrumb.message;
    if (data === breadcrumb.data && message === breadcrumb.message) {
      return breadcrumb;
    }
    return { ...breadcrumb, data, message };
  }

  function redactReplayEvent(event: ReplayFrameEvent): ReplayFrameEvent {
    const { data } = event;
    if (data.tag !== REPLAY_SPAN_TAG) {
      return event;
    }
    if (!data.payload.op.startsWith(RESOURCE_SPAN_OP_PREFIX)) {
      return event;
    }
    return {
      ...event,
      data: {
        ...data,
        payload: {
          ...data.payload,
          description: redactUrl(data.payload.description),
        },
      },
    };
  }

  function redactSpanData(data: NonNullable<SpanJson["data"]>) {
    const next = { ...data };
    let redacted = false;
    for (const key of SPAN_URL_ATTRIBUTES) {
      const value = next[key];
      if (typeof value !== "string") {
        continue;
      }
      const clean = redactUrl(value);
      if (clean === value) {
        continue;
      }
      next[key] = clean;
      redacted = true;
    }
    if (!redacted) {
      return data;
    }
    for (const key of SPAN_URL_PART_ATTRIBUTES) {
      delete next[key];
    }
    return next;
  }

  /** Tracing spans name a request `GET <url>` and repeat the URL, query and
   * fragment as attributes. */
  function redactSpan(span: SpanJson): SpanJson {
    const next = { ...span };
    if (next.description) {
      next.description = redactText(next.description);
    }
    if (next.data) {
      next.data = redactSpanData(next.data);
    }
    return next;
  }

  function redactEvent<T extends ErrorEvent | TransactionEvent>(event: T): T {
    const next = { ...event };
    if (next.request?.url) {
      next.request = { ...next.request, url: redactUrl(next.request.url) };
    }
    if (next.breadcrumbs) {
      next.breadcrumbs = next.breadcrumbs.map(redactBreadcrumb);
    }
    if (next.message) {
      next.message = redactText(next.message);
    }
    if (next.logentry) {
      next.logentry = {
        ...next.logentry,
        message: optionalText(next.logentry.message),
        params: next.logentry.params?.map(redactText),
      };
    }
    if (next.exception?.values) {
      next.exception = {
        ...next.exception,
        values: next.exception.values.map((exception) => ({
          ...exception,
          value: optionalText(exception.value),
        })),
      };
    }
    // captureConsoleIntegration stores the console call's arguments here.
    if (Array.isArray(next.extra?.arguments)) {
      next.extra = {
        ...next.extra,
        arguments: redactStrings(next.extra.arguments),
      };
    }
    return next;
  }

  function optionalText(value: string | undefined): string | undefined {
    if (value === undefined) {
      return value;
    }
    return redactText(value);
  }

  function redactErrorEvent(event: ErrorEvent): ErrorEvent {
    return redactEvent(event);
  }

  function redactTransaction(event: TransactionEvent): TransactionEvent {
    const next = redactEvent(event);
    if (next.spans) {
      next.spans = next.spans.map(redactSpan);
    }
    return next;
  }

  return {
    redactUrl,
    redactText,
    redactBreadcrumb,
    redactReplayEvent,
    redactSpan,
    redactErrorEvent,
    redactTransaction,
  };
}

function parseOrigin(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
